import { useParams } from "react-router-dom";
import { MOCK_CREWS, isCrewStreakUnlocked, isStreakAtRisk, crewDaysInactive, CREW_STREAK_THRESHOLD, STREAK_SAVER_TOKEN_INCENTIVE, ACTIVITY_TYPES, getActivityType } from "@/lib/mockData";
import PageHeader from "@/components/PageHeader";
import { MessageCircle, Send, Flame, Gift, AlertTriangle, Zap } from "lucide-react";
import { toast } from "sonner";
import { useState } from "react";

const CrewDetail = () => {
  const { id } = useParams();
  const crew = MOCK_CREWS.find((c) => c.id === id) || MOCK_CREWS[0];
  const streaked = isCrewStreakUnlocked(crew);
  const atRisk = isStreakAtRisk(crew);
  const daysOut = crewDaysInactive(crew);
  const [saved, setSaved] = useState(false);

  // Default suggested re-invite — host's most-grabbed activity type
  const suggestedType = "coffee";
  const suggestedTypeMeta = getActivityType(suggestedType);

  const saveStreak = () => {
    setSaved(true);
    toast.success(`Re-invite sent to all ${crew.members.length} members (+${STREAK_SAVER_TOKEN_INCENTIVE} tokens on accept)`);
  };

  return (
    <div data-testid="crew-detail-page">
      <PageHeader title={crew.name} subtitle={`${crew.members.length} members • last: ${crew.last_activity}`} />

      {/* Streak Saver banner — shown first when at risk */}
      {atRisk && !saved && (
        <div className="brutal-card p-5 bg-[#FFCC00]" data-testid="streak-saver-card">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-9 h-9 brutal-border bg-white rounded-lg flex items-center justify-center">
              <AlertTriangle className="w-5 h-5" strokeWidth={2.5} />
            </div>
            <div className="label-mono">Streak Saver</div>
          </div>
          <h3 className="font-heading font-black text-2xl mb-1">
            Your {crew.streak}-streak ends in {Math.max(0, 14 - daysOut)} day{Math.max(0, 14 - daysOut) === 1 ? "" : "s"}
          </h3>
          <p className="text-sm text-[#0A0A0A]/80 mb-4">
            No hang in {daysOut} days. Send a 2-tap re-invite to keep the streak (and the deal token at {crew.streak_venue}) alive — every member who accepts earns <span className="font-bold">+{STREAK_SAVER_TOKEN_INCENTIVE} tokens</span>.
          </p>

          {/* Suggested re-invite preview */}
          <div className="brutal-border bg-white rounded-lg p-3 mb-4">
            <div className="label-mono text-[#555555] mb-1">Suggested re-invite</div>
            <div className="font-heading font-bold text-base">
              {suggestedTypeMeta.emoji} Quick {suggestedTypeMeta.label.toLowerCase()} at {crew.streak_venue}
            </div>
            <div className="text-xs text-[#555555] mt-0.5">This Saturday arvo · same crew of {crew.members.length}</div>
          </div>

          <div className="flex gap-2">
            <button
              onClick={saveStreak}
              className="flex-1 brutal-btn bg-[#FF6A2C] text-white py-3 flex items-center justify-center gap-2"
              data-testid="streak-saver-send"
            >
              <Zap className="w-4 h-4" /> Send re-invite
            </button>
            <button
              onClick={() => toast("Tweak draft (mocked)")}
              className="brutal-btn bg-white px-4 py-3 text-sm"
              data-testid="streak-saver-edit"
            >
              Edit
            </button>
          </div>
        </div>
      )}

      {saved && (
        <div className="brutal-card p-5 bg-[#C8E6C9]" data-testid="streak-saver-sent">
          <div className="font-heading font-black text-2xl mb-1">Re-invite sent ✅</div>
          <p className="text-sm">We&apos;ll nudge anyone who hasn&apos;t responded after 24h. Your streak is safe for now.</p>
        </div>
      )}

      <div className={`brutal-card p-5 bg-[#C8E6C9] ${atRisk || saved ? "mt-4" : ""}`}>
        <div className="flex items-center justify-between">
          <div>
            <div className="label-mono mb-1">Crew compatibility</div>
            <div className="font-heading font-black text-4xl leading-none">{(crew.compatibility * 100).toFixed(0)}%</div>
            <div className="text-xs text-[#555555] mt-1">Average pairwise match score across all members.</div>
          </div>
          <div className="flex -space-x-3">
            {crew.members.map((m, i) => (
              <img key={m.id} src={m.avatar} className="w-12 h-12 rounded-full brutal-border object-cover bg-white" alt={m.name} style={{ zIndex: 10 - i }} />
            ))}
          </div>
        </div>
      </div>

      {/* Crew Streak card */}
      <div
        className={`brutal-card p-5 mt-4 ${streaked ? "bg-[#FF6A2C] text-white" : "bg-[#FFFBEB]"}`}
        data-testid="crew-streak-card"
      >
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <div className={`w-9 h-9 brutal-border rounded-lg flex items-center justify-center ${streaked ? "bg-white text-[#FF6A2C]" : "bg-white"}`}>
              <Flame className="w-5 h-5" strokeWidth={2.5} />
            </div>
            <div className="label-mono">Crew Streak</div>
          </div>
          <div className="font-mono font-bold text-sm">
            {crew.streak} / {CREW_STREAK_THRESHOLD}+
          </div>
        </div>

        {streaked ? (
          <>
            <h3 className="font-heading font-black text-2xl mb-1">You&apos;ve unlocked a deal token 🎉</h3>
            <p className={`text-sm mb-4 ${streaked ? "text-white/90" : "text-[#555555]"}`}>
              {crew.streak_reward} at <span className="font-bold">{crew.streak_venue}</span> — redeemed on your next crew activity at that venue.
            </p>
            <button
              onClick={() => toast.success("Deal token claimed — show this on arrival")}
              className="brutal-btn bg-white text-[#0A0A0A] px-4 py-2.5 text-sm inline-flex items-center gap-2"
              data-testid="crew-claim-token"
            >
              <Gift className="w-4 h-4" /> Claim token
            </button>
          </>
        ) : (
          <>
            <h3 className="font-heading font-black text-2xl mb-1">{CREW_STREAK_THRESHOLD - crew.streak} more to unlock a venue deal token</h3>
            <p className="text-sm text-[#555555]">
              Complete {CREW_STREAK_THRESHOLD}+ activities together and your host&apos;s linked venue offers the crew a free perk on the next hang. Tight retention loop, real reward.
            </p>
          </>
        )}
      </div>

      <div className="grid grid-cols-2 gap-3 mt-4">
        <button onClick={() => toast.success("Crew invited to new activity")} className="brutal-btn bg-[#FF6A2C] text-white py-3 flex items-center justify-center gap-2" data-testid="crew-invite-all">
          <Send className="w-4 h-4" /> Invite all
        </button>
        <button className="brutal-btn bg-white py-3 flex items-center justify-center gap-2" data-testid="crew-open-chat">
          <MessageCircle className="w-4 h-4" /> Group chat
        </button>
      </div>

      <h3 className="font-heading font-bold text-xl mt-6 mb-3">Members</h3>
      <div className="brutal-card p-4 space-y-3">
        {crew.members.map((m) => (
          <div key={m.id} className="flex items-center gap-3">
            <img src={m.avatar} className="w-10 h-10 rounded-full brutal-border object-cover" alt={m.name} />
            <div className="flex-1">
              <div className="font-bold text-sm">{m.name}, {m.age}</div>
              <div className="text-xs text-[#555555]">{m.suburb}</div>
            </div>
            <span className="font-mono text-xs brutal-border bg-[#FFFBEB] rounded-full px-2 py-0.5">94%</span>
          </div>
        ))}
      </div>

      <h3 className="font-heading font-bold text-xl mt-6 mb-3">Shared history</h3>
      <div className="brutal-card p-4 space-y-2">
        <Row title="Coffee at Bumbles" date="2 days ago" />
        <Row title="Sunday brunch in Burleigh" date="9 days ago" />
        <Row title="Friday arvo fish at The Spit" date="2 weeks ago" />
      </div>
    </div>
  );
};

const Row = ({ title, date }) => (
  <div className="flex items-center justify-between text-sm">
    <span className="font-bold">{title}</span>
    <span className="text-[#555555] font-mono text-xs">{date}</span>
  </div>
);

export default CrewDetail;
